import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { getCurrentView, CALENDAR_VIEWS } from '@helpers/javascript/calendar_helper'
import { getRequest, pathname }       from '@helpers/javascript/javascript'

const CalendarContext = createContext();

// The filter keys the feed and the pool read (view: the absences' colour in the agenda).
const FEED_FILTER_KEYS = ['unchecked', 'view', 'manager', 'agency', 'category', 'type', 'site', 'address', 'radius', 'text', 'showReceivedParts']
const POOL_FILTER_KEYS = ['showOpen', 'showReceivedParts', 'agency', 'category', 'manager', 'type', 'site', 'address', 'radius', 'text', 'period']

const feedKeyOf = (filter, ignoreFilter) =>
  ignoreFilter ? 'unfiltered' : JSON.stringify(FEED_FILTER_KEYS.map(key => key === 'view' ? filter.view.name : filter[key]))
const poolKeyOf = filter => JSON.stringify(POOL_FILTER_KEYS.map(key => filter[key]))

export const CalendarProvider = ({ children, props }) => {
  const didMountRef         = useRef(false)
  const categoryMountedRef  = useRef(false)
  const { filterSettings, preferences, toolSettings } = props

  const [throttle,      setThrottle]      = useState()

  const [filter, setFilter] = useState(() => ({
    unchecked:         filterSettings.unchecked,
    filterCalendar:    filterSettings.filterCalendar,
    showOpen:          filterSettings.showOpen,
    showReceivedParts: filterSettings.showReceivedParts,
    showWeekends:      filterSettings.showWeekends,
    autoRefresh:       filterSettings.autoRefresh,
    radius:            filterSettings.radius,
    text:              filterSettings.text,
    address:           filterSettings.address,
    period:            filterSettings.period,
    slotMinTime:       filterSettings.slotMinTime,
    slotMaxTime:       filterSettings.slotMaxTime,
    agency:            filterSettings.agency,
    manager:           filterSettings.manager,
    category:          filterSettings.category,
    site:              filterSettings.site,
    type:              filterSettings.type,
    resourcesOnly:     filterSettings.resourcesOnly || false,
    projectsOnly:      (!!toolSettings.projects && filterSettings.projectsOnly) || false,
    showResources:     filterSettings.showResources !== false,
    stackedAgenda:     filterSettings.stackedAgenda || false,
    view:              getCurrentView(filterSettings.view, preferences.slotWidthWeek, preferences.slotWidthMonth)
  }))

  const [state, setState] = useState({
    showPopup:             false,
    filterOpen:            false,
    settingsOpen:          false,
    filterResponse:        '',
    calendar:              null,
    currentDate:           null,
    startDate:             null,
    endDate:               null,
    showSmallCard:         false,
    events:                null,
    openEvents:            null,
    openEventsTotal:       0,
    openEventsPage:        1,
    scrollToResourceId:    null,
    absenceModal:          null, // {technicianId, startDate, endDate, onCallDuty, anchorDate}
    leftCollapsed:         localStorage.getItem('calendar-left-collapsed') === '1'
  })

  // Absences: opened from the toolbar, from an Alt+drag on a technician row,
  // or by clicking a technician in the Équipes column.
  const openAbsenceModal  = (prefill = {}) => setState(prev => ({ ...prev, absenceModal: prefill }))
  const closeAbsenceModal = () => setState(prev => ({ ...prev, absenceModal: null }))

  // Collapse / expand the whole left column (mini calendar, settings, filters,
  // technician list). FullCalendar recomputes its width on window resize, so we
  // fire one after the grid has settled.
  const toggleLeftColumn = () => {
    setState(prevState => {
      const leftCollapsed = !prevState.leftCollapsed
      try { localStorage.setItem('calendar-left-collapsed', leftCollapsed ? '1' : '0') } catch (_) {}
      setTimeout(() => window.dispatchEvent(new Event('resize')), 50)
      return { ...prevState, leftCollapsed }
    })
  }

  const TOOLS = ({
    slotWidthWeek:         preferences.slotWidthWeek,
    slotWidthMonth:        preferences.slotWidthMonth,
    workorderFromCalendar: preferences.workorderFromCalendar,
    displayOpen:           preferences.displayOpenWorkordersInCalendar,
    showPopovers:          preferences.showCalendarPopovers,
    partsStatusPermitted:  toolSettings.partsStatusPermitted,
    newAbsences:           toolSettings.newAbsences,
    projects:              toolSettings.projects,
    license:               toolSettings.license,
    timeZone:              toolSettings.timeZone,
    categories:            toolSettings.categories,
    agencies:              toolSettings.agencies,
    resources:             toolSettings.resources,
    droppableResources:    toolSettings.droppableResources,
    suggestPlanning:       toolSettings.suggestPlanning,
    initialView:           filterSettings.view
  })

  // Timers send the filter and week on screen when they fire, not when scheduled.
  const latest = useRef(null)
  latest.current = { filter, startDate: state.startDate, endDate: state.endDate }

  // An answer never replaces a newer one already shown.
  const feedRequest = useRef(0)
  const feedApplied = useRef(0)
  const poolRequest = useRef(0)
  const sentFeedKey = useRef(null)
  const sentPoolKey = useRef(null)
  // The filter of the pool on screen: "load more" pages through it.
  const poolFilter  = useRef(null)

  const fetchEvents = ({start, end, callback, ignoreFilter}) => {
    const current      = latest.current.filter
    const path         = '/calendar/event_list'
    const filterParams = {...current}
    filterParams.view  = current.view.name
    const params       = ignoreFilter ? {} : filterParams

    if (start && end) {
      params.start = start
      params.end   = end
    }

    const url     = pathname({ path, params })
    const request = ++feedRequest.current
    const key     = feedKeyOf(current, ignoreFilter)
    sentFeedKey.current = key
    getRequest(url).then(res => {
      if (request < feedApplied.current) return
      feedApplied.current = request
      callback(res)
    })
    .catch(() => { if (sentFeedKey.current === key) sentFeedKey.current = null })
  }

  // page 1 replaces the list; further pages append ("load more").
  const fetchOpenEvents = (page = 1) => {
    const path    = '/calendar/open_workorders'
    const more    = page > 1 && !!poolFilter.current
    const current = more ? poolFilter.current : latest.current.filter
    const params  = { ...current, open_page: page }
    const key     = poolKeyOf(current)
    const request = more ? poolRequest.current : ++poolRequest.current
    if (!more) sentPoolKey.current = key
    const url    = pathname({ path, params })
    getRequest(url).then(res => {
      if (request !== poolRequest.current) return
      if (more && poolFilter.current !== current) return
      if (!more) poolFilter.current = current
      setState(prevState => {
        const events = res.events || res // tolerate the legacy array shape
        return {
          ...prevState,
          openEvents:      page > 1 ? [...(prevState.openEvents || []), ...events] : events,
          openEventsTotal: res.total ?? events.length,
          openEventsPage:  page,
        }
      })
    })
    // Without this a failed request reads as "the filter did not apply".
    .catch(() => {
      if (request !== poolRequest.current) return
      if (!more && sentPoolKey.current === key) sentPoolKey.current = null
      flashAlert(I18n.t('errors.messages.oops'))
    })
  }

  const fetchMoreOpenEvents = () => fetchOpenEvents(state.openEventsPage + 1)

  const fetchAllEvents = () => {
    const { filter: current, startDate, endDate } = latest.current
    fetchEvents({
      start:        startDate,
      end:          endDate,
      callback:     (e)=>setState(prevState=>({...prevState, events: e})),
      ignoreFilter: !current.filterCalendar
    })
  }

  useEffect(() => {
    if (didMountRef.current) {
      if (throttle) clearTimeout(throttle);

      setState(prevState => ({
        ...prevState,
        filterResponse: ''
      }));

      setThrottle(setTimeout(() => {
        const current = latest.current.filter
        if (feedKeyOf(current, !current.filterCalendar) !== sentFeedKey.current) fetchAllEvents();
        if (current.showOpen || current.showReceivedParts) {
          if (poolKeyOf(current) !== sentPoolKey.current) fetchOpenEvents();
        } else {
          sentPoolKey.current = null
        }
      }, 500));
    } else {
      if (filter.showOpen || filter.showReceivedParts) fetchOpenEvents();
      didMountRef.current = true;
    }
  }, [filter]);

  useEffect(() => {
    if (!!filter.autoRefresh) {
      const refreshInterval = 15000
      const refetchEvents = setInterval(() => {
        // Don't hammer the server while the tab is in the background.
        if (document.hidden) return
        const { filter: current, startDate, endDate } = latest.current
        // Filtered or not, as the feed on screen.
        fetchEvents({
          start:        startDate,
          end:          endDate,
          callback:     (e)=>setState(prevState=>({...prevState, events: e})),
          ignoreFilter: sentFeedKey.current === null ? !current.filterCalendar : sentFeedKey.current === feedKeyOf(null, true)
        })
      }, refreshInterval)
      return () => clearInterval(refetchEvents);
    }
  }, [filter.autoRefresh]);

  useEffect(() => {
    if (state.currentDate) {
      let url = new URL(window.location)
      url.searchParams.set('selected_date', state.currentDate.toISOString().split('T')[0]);
      history.replaceState(history.state, document.title, url.href)
    }
  }, [state.currentDate])

  const matchesCategoryFilter = resource => {
    if (!filter.category) return true
    if (!resource.domains || resource.domains.length === 0) return true
    return resource.domains.some(d => d.id == filter.category)
  }

  useEffect(() => {
    if (!categoryMountedRef.current) {
      categoryMountedRef.current = true
      return
    }

    const technicians    = TOOLS.resources.filter(resource => resource.parentId)
    const matchingIds    = technicians.filter(matchesCategoryFilter).map(r => r.id)
    const nonMatchingIds = technicians.filter(r => !matchesCategoryFilter(r)).map(r => r.id)
    // Managers of the matching technicians must stay visible, otherwise their
    // technician rows are orphaned in FullCalendar and nothing is displayed.
    const managerIds     = [...new Set(
      TOOLS.resources.filter(r => matchingIds.includes(r.id)).map(r => r.parentId).filter(Boolean)
    )]

    setFilter(prev => ({
      ...prev,
      unchecked: [...new Set([
        ...prev.unchecked.filter(id => !matchingIds.includes(id) && !managerIds.includes(id)),
        ...nonMatchingIds.filter(id => !managerIds.includes(id))
      ])]
    }))
  }, [filter.category])

  return (
    <CalendarContext.Provider value={{ filter, setFilter, TOOLS, state, setState, fetchEvents, fetchAllEvents, fetchMoreOpenEvents, toggleLeftColumn, matchesCategoryFilter, openAbsenceModal, closeAbsenceModal }}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};
